/**
 * Chat panel component for the AI Assistant.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Drawer, Input, Modal, Spin, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  CloseOutlined,
  CrownOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { MessageBubble } from './MessageBubble';
import { useAssistantCapabilities } from '../hooks/useAssistantCapabilities';
import { useAssistantChat } from '../hooks/useAssistantChat';
import { useSuggestedMessages } from '../hooks/useSuggestedMessages';
import { useAssistantSupport } from '../hooks/useAssistantSupport';
import { analytics } from '@tools/analytics.ts';
import type {
  AssistantPublicAction,
  SuggestedMessage,
} from '@app-types/assistant.ts';
import ChatbotAvatar from '../avatars/DialoguinAvatarRedesign';
import type { MoodType } from '../types/types';
import './AssistantWidget.css';

const { TextArea } = Input;

export interface ChatPanelProps {
  open: boolean;
  onClose: () => void;
  isStaff?: boolean;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  open,
  onClose,
  isStaff = false,
}) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shownSuggestionKeysRef = useRef<Set<string>>(new Set());

  const {
    sessionId,
    messages,
    loading,
    error,
    initSession,
    sendMessage,
    endConversation,
    endAndStartNew,
    submitFeedback,
    resolveAction,
    hasSession,
    isEmpty,
    maxMessageCharacters,
  } = useAssistantChat();
  const shouldOfferSupport =
    Boolean(error) ||
    messages.some((message) => message.feedback?.rating === 'not_helpful');
  const supportQuery = useAssistantSupport(open && shouldOfferSupport);
  const { capabilities } = useAssistantCapabilities({ enabled: open });

  const { suggestedMessages } = useSuggestedMessages(
    capabilities?.suggestions ?? []
  );
  const avatarMood: MoodType = loading ? 'thinking' : 'happy';
  const normalizedContext = capabilities?.normalized_context;
  const suggestionAnalyticsContext = useMemo(() => {
    if (!normalizedContext) return 'unknown';
    return [
      normalizedContext.area,
      normalizedContext.module,
      normalizedContext.screen,
    ]
      .filter(Boolean)
      .join('.');
  }, [normalizedContext]);

  useEffect(() => {
    if (open && !hasSession) {
      initSession();
    }
  }, [open, hasSession, initSession]);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      // Scroll inmediato al abrir
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      // Scroll después de un breve delay por la animación del Drawer
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !isEmpty || !suggestedMessages.length) return;

    suggestedMessages.forEach((suggestion) => {
      if (!suggestion.suggestionId) return;

      const eventKey = [
        suggestion.suggestionId,
        suggestionAnalyticsContext,
        capabilities?.audience ?? 'unknown',
        sessionId ?? 'no_session',
      ].join('|');
      if (shownSuggestionKeysRef.current.has(eventKey)) return;
      shownSuggestionKeysRef.current.add(eventKey);

      const eventPayload: Record<string, string | number> = {
        suggestion_id: suggestion.suggestionId,
        context: suggestionAnalyticsContext,
      };
      if (suggestion.capability)
        eventPayload.capability = suggestion.capability;
      if (suggestion.intentHint)
        eventPayload.intent_hint = suggestion.intentHint;
      if (capabilities?.audience) eventPayload.role = capabilities.audience;
      if (sessionId !== null) eventPayload.session_id = sessionId;

      analytics?.track('assistant_suggestion_shown', eventPayload);
    });
  }, [
    capabilities?.audience,
    hasSession,
    isEmpty,
    open,
    sessionId,
    suggestedMessages,
    suggestionAnalyticsContext,
  ]);

  const lastQuestions = [...messages]
    .reverse()
    .find(
      (message) => message.role === 'assistant' && message.questions?.length
    )?.questions;

  const handleSend = async () => {
    if (!inputValue.trim() || loading) return;

    const message = inputValue.trim();
    try {
      await sendMessage(message);
      setInputValue('');
    } catch {
      // The hook exposes a safe user-facing error and keeps the draft.
    }
  };

  const handleSuggestedClick = async (suggestion: SuggestedMessage) => {
    if (
      loading ||
      !hasSession ||
      !suggestion.suggestionId ||
      !suggestion.intentHint
    )
      return;

    const eventPayload: Record<string, string | number> = {
      suggestion_id: suggestion.suggestionId,
      intent_hint: suggestion.intentHint,
      context: suggestionAnalyticsContext,
    };
    if (suggestion.capability) eventPayload.capability = suggestion.capability;
    if (capabilities?.audience) eventPayload.role = capabilities.audience;
    if (sessionId !== null) eventPayload.session_id = sessionId;

    analytics?.track('assistant_suggestion_clicked', eventPayload);

    try {
      await sendMessage(suggestion.text, true, {
        suggestion_id: suggestion.suggestionId,
        intent_hint: suggestion.intentHint,
      });
    } catch {
      // The hook exposes a safe user-facing error.
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAction = async (
    messageId: number,
    action: AssistantPublicAction
  ) => {
    if (action.kind === 'help') {
      await sendMessage(action.label, true, {
        suggestion_id: action.suggestion_id,
        intent_hint: 'help',
      });
      return;
    }
    const resolved = await resolveAction(messageId, action.id);
    if (resolved.kind === 'navigate' && resolved.path) {
      navigate(resolved.path);
    } else if (resolved.kind === 'support' && resolved.url) {
      window.open(resolved.url, '_blank', 'noopener,noreferrer');
    }
  };

  const roleIcon = isStaff ? <CrownOutlined /> : <UserOutlined />;

  const handleCloseRequest = () => {
    if (!hasSession) {
      onClose();
      return;
    }
    Modal.confirm({
      title: 'Cerrar el asistente',
      content:
        'Podés continuar dentro de las próximas 24 horas o terminar esta conversación.',
      okText: 'Continuar luego',
      cancelText: 'Terminar conversación',
      closable: false,
      maskClosable: false,
      keyboard: false,
      onOk: onClose,
      onCancel: async () => {
        try {
          await endConversation();
          onClose();
        } catch {
          // Keep the panel open so the user can retry the idempotent close.
        }
      },
    });
  };

  return (
    <Drawer
      title={
        <div className="assistant-panel__header">
          <div className="assistant-panel__header-avatar">
            <ChatbotAvatar mood={avatarMood} />
          </div>
          <div className="assistant-panel__header-copy">
            <div className="assistant-panel__title-row">
              <span className="assistant-panel__title">Asistente IA</span>
              <Tag className="assistant-panel__role-tag" icon={roleIcon}>
                {isStaff ? 'Staff' : 'Cliente'}
              </Tag>
            </div>
            <span className="assistant-panel__status">En línea</span>
          </div>
        </div>
      }
      placement="right"
      onClose={handleCloseRequest}
      open={open}
      zIndex={1250}
      size={360}
      closeIcon={<CloseOutlined />}
      rootClassName="assistant-panel-drawer"
      mask={false}
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <div className="assistant-panel__messages">
        {error && (
          <Alert
            title="No se pudo responder"
            description={error}
            type="error"
            closable
            className="assistant-panel__alert"
          />
        )}

        {shouldOfferSupport && supportQuery.data?.contacts.length ? (
          <div className="assistant-panel__support" aria-label="Canales de soporte">
            <span>¿Necesitás ayuda de una persona?</span>
            <div>
              {supportQuery.data.contacts.map((contact) => (
                <Button
                  key={contact.id}
                  size="small"
                  href={contact.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {contact.label}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {loading && !messages.length && (
          <div className="assistant-panel__loading">
            <Spin size="small" />
            <span>Iniciando conversación...</span>
          </div>
        )}

        {!loading && isEmpty && !error && (
          <div className="assistant-panel__empty">
            <div className="assistant-panel__empty-card">
              <div className="assistant-panel__empty-avatar">
                <ChatbotAvatar mood="happy" />
              </div>
              <div>
                <strong>Hola, soy el asistente de ayuda.</strong>
                <span>
                  Consultame cómo usar las funciones disponibles para tu cuenta.
                </span>
              </div>
            </div>

            <div
              className="assistant-panel__suggestions"
              aria-label="Mensajes sugeridos"
            >
              {suggestedMessages.map((suggestion, idx) => (
                <Button
                  key={`${suggestion.text}-${idx}`}
                  type="text"
                  block
                  onClick={() => handleSuggestedClick(suggestion)}
                  className="assistant-panel__suggestion-button"
                >
                  {suggestion.icon && (
                    <span className="assistant-panel__suggestion-icon">
                      {suggestion.icon}
                    </span>
                  )}
                  <span>{suggestion.text}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            onEndAndStartNew={() => {
              void endAndStartNew();
            }}
            onFeedback={submitFeedback}
            onAction={handleAction}
          />
        ))}

        {lastQuestions?.length ? (
          <div className="assistant-panel__questions">
            {lastQuestions.map((question) => (
              <div className="assistant-panel__question" key={question.id}>
                <span className="assistant-panel__question-label">
                  {question.text}
                </span>
                {question.options?.length ? (
                  <div className="assistant-panel__question-options">
                    {question.options.map((option) => (
                      <Button
                        key={option.id}
                        size="small"
                        type="text"
                        onClick={() => setInputValue(option.label)}
                        className="assistant-panel__option-button"
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <div ref={messagesEndRef} />
      </div>

      <div className="assistant-panel__composer">
        <div className="assistant-panel__composer-row">
          <TextArea
            value={inputValue}
            onChange={(e) => {
              if (Array.from(e.target.value).length <= maxMessageCharacters) {
                setInputValue(e.target.value);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Escribe tu mensaje..."
            showCount={{
              formatter: ({ value }) =>
                `${Array.from(value).length}/${maxMessageCharacters}`,
            }}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading || !hasSession}
            className="assistant-panel__textarea"
          />
          <Button
            type="primary"
            icon={loading ? <Spin size="small" /> : <SendOutlined />}
            onClick={handleSend}
            disabled={!inputValue.trim() || loading || !hasSession}
            aria-label="Enviar mensaje"
            className="assistant-panel__send-button"
          />
        </div>

        {loading && messages.length > 0 && (
          <div className="assistant-panel__thinking">
            <Spin size="small" />
            <span>El asistente está pensando...</span>
          </div>
        )}
      </div>
    </Drawer>
  );
};
